import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as ChevronIcon } from 'assets/icons/chevron.svg';
import { clsx } from 'clsx';
import dayjs from 'dayjs';

import { isHighSeverityOwnershipFinding } from '../utils/monitoringResultsUtils';

import type IMonitoringOwnershipFindingCard from './interfaces/IMonitoringOwnershipFindingCard';

import styles from './sass/MonitoringOwnershipFindingCard.module.scss';

function MonitoringOwnershipFindingCard({
  finding,
  defaultExpanded = isHighSeverityOwnershipFinding(finding),
}: IMonitoringOwnershipFindingCard): React.JSX.Element {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState<boolean>(defaultExpanded);

  const formattedDate = dayjs(finding.observed_at).isValid()
    ? dayjs(finding.observed_at).format('MMM D, YYYY')
    : finding.observed_at;

  const themeLabel = t(`valueChainMap.monitoring.results.ownershipTheme.${finding.theme}`, {
    defaultValue: finding.theme,
  });

  return (
    <article
      className={clsx(styles.container, {
        [styles.riskCritical]: finding.severity === 'critical',
        [styles.riskHigh]: finding.severity === 'high',
        [styles.riskMedium]: finding.severity === 'medium',
        [styles.riskLow]: finding.severity === 'low',
      })}
    >
      <button
        type="button"
        className={styles.header}
        onClick={() => setIsExpanded((prevExpanded) => !prevExpanded)}
        aria-expanded={isExpanded}
      >
        <div className={styles.headerMain}>
          {finding.source_url ? (
            <a
              href={finding.source_url}
              className={styles.titleLink}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(event) => event.stopPropagation()}
            >
              {finding.title}
            </a>
          ) : (
            <h3 className={styles.title}>{finding.title}</h3>
          )}

          <span className={styles.supplierName}>{finding.supplier_name}</span>

          <div className={styles.metaRow}>
            <span>{formattedDate}</span>
            {finding.source_label ? (
              <>
                <span aria-hidden="true">·</span>
                <span>{finding.source_label}</span>
              </>
            ) : null}
          </div>

          <div className={styles.badgeRow}>
            <span className={clsx(styles.badge, styles.themeBadge)}>{themeLabel}</span>
            <span className={clsx(styles.badge, styles[`risk_${finding.severity}`])}>
              {t(`valueChainMap.monitoring.results.riskSeverity.${finding.severity}`)}
            </span>
            {typeof finding.confidence === 'number' ? (
              <span className={styles.confidence}>
                {t('valueChainMap.monitoring.results.confidence', {
                  value: Math.round(finding.confidence * 100),
                })}
              </span>
            ) : null}
          </div>
        </div>

        <ChevronIcon
          className={clsx(styles.chevron, { [styles.chevronExpanded]: isExpanded })}
          aria-hidden="true"
        />
      </button>

      {isExpanded ? (
        <div className={styles.body}>
          {finding.link_path?.length ? (
            <div className={styles.linkPathBox}>
              <span className={styles.linkPathLabel}>
                {t('valueChainMap.monitoring.results.ownershipLinkPath')}
              </span>
              <div className={styles.linkPathTrail}>
                {finding.link_path.map((node, nodeIndex) => (
                  <React.Fragment key={`${finding.id}-${node.entity_name}`}>
                    <span className={styles.linkPathNode}>
                      <span className={styles.linkPathRole}>{node.role}</span>
                      <span className={styles.linkPathName}>{node.entity_name}</span>
                    </span>
                    {nodeIndex < finding.link_path!.length - 1 ? (
                      <span className={styles.linkPathArrow} aria-hidden="true">→</span>
                    ) : null}
                  </React.Fragment>
                ))}
              </div>
            </div>
          ) : null}

          {finding.contagion_mechanism ? (
            <p className={styles.mechanism}>
              <strong>
                {t('valueChainMap.monitoring.results.ownershipContagionMechanism')}
                :
                {' '}
              </strong>
              {finding.contagion_mechanism}
            </p>
          ) : null}

          {finding.shortage_risk?.summary ? (
            <div className={styles.shortageBox}>
              <span className={styles.shortageLabel}>
                {t('valueChainMap.monitoring.results.ownershipShortageRisk')}
                {finding.shortage_risk.level ? ` · ${finding.shortage_risk.level}` : ''}
              </span>
              <p className={styles.shortageSummary}>{finding.shortage_risk.summary}</p>
            </div>
          ) : null}

          <div className={styles.detailGrid}>
            {finding.related_materials?.length ? (
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>
                  {t('valueChainMap.monitoring.results.relatedMaterials')}
                </span>
                <span className={styles.detailValue}>
                  {finding.related_materials.join(', ')}
                </span>
              </div>
            ) : null}

            {finding.source_label || finding.source_url ? (
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>
                  {t('valueChainMap.monitoring.results.sources')}
                </span>
                {finding.source_url ? (
                  <a
                    href={finding.source_url}
                    className={styles.sourceLink}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {finding.source_label || finding.source_url}
                  </a>
                ) : (
                  <span className={styles.detailValue}>{finding.source_label}</span>
                )}
              </div>
            ) : null}
          </div>

          {finding.recommended_action ? (
            <p className={styles.recommendedAction}>
              <strong>
                {t('valueChainMap.monitoring.results.ownershipRecommendedAction')}
                :
                {' '}
              </strong>
              {finding.recommended_action}
            </p>
          ) : null}
        </div>
      ) : null}
    </article>
  );
}

export default MonitoringOwnershipFindingCard;
